import React from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/compat/router';

import useUI from 'hooks/use-ui';
import { useDispensaryCategory } from 'src/hooks/use-dispensary-category';
import { useCollectionFallbackForProductsRoute } from 'src/dispensary/hooks/use-collection-fallback-for-products-route';
import { useCommerceComponents } from 'src/theme/provider/commerce-components/context';
import {
  CategoryPageBelowListMatchTarget,
  findMatchingCategoryPageBelowList,
  getCategoryToMatch,
} from './category-page-below-list';

/**
 * Resolves the matching components for the CategoryPageBelowList region (ECOMM-13290).
 *
 * Scoped to storefront PLPs (category, subcategory, collection):
 * - router ready;
 * - storefront only (isStoreFront === true, isKiosk === false);
 * - not a specials, brand, or search/all-products route;
 * - matches exact subcategory on subcategory routes (/products/:category/:subcategory);
 * - matches exact collection slug on collection routes (/collections/:slug and /products/:slug fallback);
 * - matches exact category on top-level category routes (/products/:category);
 * - pagination page does not affect resolution (renders on page 1, 2, ...);
 * - fails closed to [] for missing, unmatched, or invalid configuration.
 */
export function useCategoryPageBelowListComponents(): React.ElementType[] {
  const router = useRouter();
  const { iterables } = useCommerceComponents();
  const UI = useUI();
  const { isKiosk, isStoreFront } = UI;
  const category = useDispensaryCategory();
  const collectionFallback = useCollectionFallbackForProductsRoute(category);

  if (!router?.isReady || !isStoreFront || isKiosk) {
    return [];
  }

  // Exclude specials, brand, and search pages
  const pathname = (router.pathname as string | undefined) ?? '';
  if (pathname.includes('/specials') || Boolean(router.query.brandId) || Boolean(UI.onSearchPage)) {
    return [];
  }

  const entries = iterables.CategoryPageBelowList();
  const subcategory = typeof router.query.subcategory === 'string' ? router.query.subcategory : undefined;

  // Case 1: Subcategory route (/products/:category/:subcategory)
  if (subcategory) {
    const categoryToMatch = getCategoryToMatch(router.query);
    const target: CategoryPageBelowListMatchTarget = {
      category: categoryToMatch,
      subcategory,
    };
    return findMatchingCategoryPageBelowList(entries, target);
  }

  // Case 2: Collection route (/collections/:slug or /products/:slug collection fallback)
  const isDirectCollectionRoute = pathname.includes('/collections/');
  if (isDirectCollectionRoute) {
    const collectionSlug = typeof router.query.slug === 'string' ? router.query.slug : undefined;
    if (collectionSlug) {
      return findMatchingCategoryPageBelowList(entries, { collection: collectionSlug });
    }
  }

  if (collectionFallback) {
    return findMatchingCategoryPageBelowList(entries, { collection: collectionFallback.slug });
  }

  // Case 3: Top-level category route (/products/:category)
  if (typeof router.query.category === 'string' && category) {
    const categoryToMatch = getCategoryToMatch(router.query);
    return findMatchingCategoryPageBelowList(entries, { category: categoryToMatch });
  }

  return [];
}

/**
 * Renders the generic remote component region below the complete product list
 * and inline categories list on top-level storefront category pages (ECOMM-13290).
 */
export function CategoryPageBelowList(): JSX.Element | null {
  const components = useCategoryPageBelowListComponents();

  if (components.length === 0) {
    return null;
  }

  return (
    <BelowListContainer data-testid='category-page-below-list'>
      {components.map((component, index) => {
        const Component = component;
        return (
          // eslint-disable-next-line react/no-array-index-key
          <Component key={`category-below-list-${index}`} />
        );
      })}
    </BelowListContainer>
  );
}

const BelowListContainer = styled.div`
  width: 100%;
  max-width: var(--dct--customized-maxSiteWidth);
  margin-inline: auto;
  box-sizing: border-box;
`;
